import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import moment from "moment";
import SelectBox from "elements/SelectBox";
import { __ } from "services/Translator";
import "react-datepicker/dist/react-datepicker.css";

const toStoredValue = (value) => {
  if (!value) {
    return "";
  }
  const parts = value.toString().split("-");
  const year = parts[0];
  const month = parts[1];
  const day = parts[2];
  if (!year || year === "0000" || month === "00" || day === "00") {
    return "";
  }
  return `${year}-${month}-${day}`;
};

const DateSelector = (props) => {
  const {
    useMobilePicker: useMobilePickerProp,
    className = "",
    value,
    onChange,
    startYear: startYearProp,
    endYear: endYearProp,
    placeholder,
    disabled,
  } = props;

  const [useMobilePicker, setUseMobilePicker] = useState(false);

  useEffect(() => {
    if (useMobilePickerProp !== undefined) {
      setUseMobilePicker(Boolean(useMobilePickerProp));
      return undefined;
    }
    const mediaQuery = window.matchMedia("(max-width: 767px)");
    const syncViewport = () => setUseMobilePicker(mediaQuery.matches);
    syncViewport();
    mediaQuery.addEventListener("change", syncViewport);
    return () => mediaQuery.removeEventListener("change", syncViewport);
  }, [useMobilePickerProp]);

  const startYear =
    startYearProp && !isNaN(parseInt(startYearProp, 10))
      ? parseInt(startYearProp, 10)
      : 1950;
  const endYear =
    endYearProp && !isNaN(parseInt(endYearProp, 10))
      ? parseInt(endYearProp, 10)
      : new Date().getFullYear();

  const minDate = moment(`${startYear}-01-01`, "YYYY-MM-DD").toDate();
  const maxDate = moment(`${endYear}-12-31`, "YYYY-MM-DD").toDate();
  const storedValue = toStoredValue(value);
  const selected =
    storedValue && moment(storedValue, "YYYY-MM-DD", true).isValid()
      ? moment(storedValue, "YYYY-MM-DD").toDate()
      : null;

  const handlePickerChange = (date) => {
    if (!date) {
      onChange?.(null);
      return;
    }
    onChange?.(moment(date).format("YYYY-MM-DD"));
  };

  if (useMobilePicker) {
    return (
      <div className={`date-selector-mobile ${className}`.trim()}>
        <DatePicker
          selected={selected}
          onChange={handlePickerChange}
          dateFormat="MMM d, yy"
          placeholderText={placeholder || __("Any date")}
          minDate={minDate}
          maxDate={maxDate}
          disabled={disabled}
          withPortal
          isClearable
          showPopperArrow={false}
          className="form-control name_value date-selector-mobile__picker"
          calendarClassName="date-selector-mobile__calendar"
          popperClassName="date-selector-mobile__popper"
        />
      </div>
    );
  }

  const parts = value
    ? value.toString().split("-")
    : ["0000", "00", "00"];

  const updateValue = (newValue, field) => {
    if (onChange) {
      const valueUpdate = JSON.parse(JSON.stringify(parts));
      valueUpdate[field] = newValue;
      onChange(valueUpdate.join("-"));
    }
  };

  const dates = [...Array(31).keys()].map((val) => {
    return {
      label: (val + 1).toString(),
      value: (val + 1).toString().padStart(2, "0"),
    };
  });
  dates.unshift({ label: __("Date"), value: "00" });

  const months = [
    { label: __("Month"), value: "00" },
    { label: __("January"), value: "01" },
    { label: __("February"), value: "02" },
    { label: __("March"), value: "03" },
    { label: __("April"), value: "04" },
    { label: __("May"), value: "05" },
    { label: __("June"), value: "06" },
    { label: __("July"), value: "07" },
    { label: __("August"), value: "08" },
    { label: __("September"), value: "09" },
    { label: __("October"), value: "10" },
    { label: __("November"), value: "11" },
    { label: __("December"), value: "12" },
  ];

  const years = [...Array(endYear - startYear + 1).keys()].map((val) => {
    return {
      label: (val + startYear).toString(),
      value: (val + startYear).toString(),
    };
  });
  years.unshift({ label: __("Year"), value: "0000" });
  if (props.reverseYear) {
    years.reverse();
  }

  return (
    <div className={`row filter_group ${className}`.trim()}>
      <div className="col-4">
        <SelectBox
          className="form-control name_value"
          items={dates}
          value={parts[2]}
          onChange={(itemValue) => updateValue(itemValue, 2)}
          disabled={disabled}
        />
      </div>
      <div className="col-4">
        <SelectBox
          className="form-control name_value"
          items={months}
          value={parts[1]}
          onChange={(itemValue) => updateValue(itemValue, 1)}
          disabled={disabled}
        />
      </div>
      <div className="col-4">
        <SelectBox
          className="form-control name_value"
          items={years}
          value={parts[0]}
          onChange={(itemValue) => updateValue(itemValue, 0)}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

export default DateSelector;
